import { createFileRoute } from '@tanstack/react-router'
import { Plus, ChevronRight, ChevronDown, Building2, Users, Trash2, Edit } from 'lucide-react'
import { useState } from 'react'

import { DepartmentForm } from '@/components/hr/department/department-form'
import { Button } from '@/components/ui/button'
import { useConfirmDialog } from '@/hooks'
import {
  useDepartmentTree,
  useDeleteDepartment,
  type DepartmentTreeNode,
} from '@/hooks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/hr/departments/')({
  component: DepartmentListPage,
})

function DepartmentListPage() {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingDepartment, setEditingDepartment] = useState<DepartmentTreeNode | null>(null)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const { data: tree, isLoading, error } = useDepartmentTree()
  const deleteMutation = useDeleteDepartment()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) }
      else { next.add(id) }
      return next
    })
  }

  const handleDelete = async (id: string, name: string) => {
    const confirmed = await confirmWithPreset(
      'delete',
      `确定要删除部门"${name}"吗？`
    )
    if (confirmed) {
      setLoading(true)
      try {
        await deleteMutation.mutateAsync(id)
      } finally {
        setLoading(false)
      }
    }
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">部门管理</h1>
          <p className="text-muted-foreground">管理公司组织架构</p>
        </div>
        <Button
          onClick={() => setShowCreateForm(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          新建部门
        </Button>
      </div>

      {showCreateForm && (
        <DepartmentForm onClose={() => setShowCreateForm(false)} onSuccess={() => setShowCreateForm(false)} />
      )}

      {editingDepartment && (
        <DepartmentForm
          department={editingDepartment}
          onClose={() => setEditingDepartment(null)}
          onSuccess={() => setEditingDepartment(null)}
        />
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : !tree || tree.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <Building2 className="h-12 w-12 text-muted-foreground" />
          <div className="text-center">
            <p className="text-lg font-medium">暂无部门</p>
            <p className="text-sm text-muted-foreground">点击上方按钮创建第一个部门</p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border">
          <DepartmentTree
            nodes={tree}
            expandedIds={expandedIds}
            onToggle={toggleExpand}
            onEdit={setEditingDepartment}
            onDelete={handleDelete}
          />
        </div>
      )}
      <ConfirmDialog />
    </div>
  )
}

interface DepartmentTreeProps {
  nodes: DepartmentTreeNode[]
  expandedIds: Set<string>
  onToggle: (id: string) => void
  onEdit: (dept: DepartmentTreeNode) => void
  onDelete: (id: string, name: string) => Promise<void>
  level?: number
}

function DepartmentTree({ nodes, expandedIds, onToggle, onEdit, onDelete, level = 0 }: DepartmentTreeProps) {
  return (
    <div>
      {nodes.map((node) => {
        const hasChildren = node.children && node.children.length > 0
        const isExpanded = expandedIds.has(node.id)

        return (
          <div key={node.id}>
            <div
              className={cn(
                'flex items-center justify-between border-b px-4 py-3 hover:bg-muted/30',
                level > 0 && 'border-l-2 border-l-muted'
              )}
              style={{ paddingLeft: `${level * 24 + 16}px` }}
            >
              <div className="flex items-center gap-2">
                {hasChildren ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onToggle(node.id)}
                    className="h-6 w-6 p-0.5"
                  >
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </Button>
                ) : (
                  <span className="w-5" />
                )}
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{node.name}</span>
                <span className="text-sm text-muted-foreground">({node.code})</span>
                {node.employeeCount !== undefined && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Users className="h-3 w-3" />
                    {node.employeeCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(node)}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => void onDelete(node.id, node.name)}
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {hasChildren && isExpanded && (
              <DepartmentTree
                nodes={node.children}
                expandedIds={expandedIds}
                onToggle={onToggle}
                onEdit={onEdit}
                onDelete={onDelete}
                level={level + 1}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
